import { colors, type, viz } from "../styles";
import { formatDateTime } from "../format";
import { DmMessageRow } from "../types";

// A tint of the dashboard's blue series for Markit's own sends. The dashboard's
// accent is black, which as a bubble fill would drown the text.
const MARKIT_BG = "#EAF2FC";
const MARKIT_BORDER = "#BBD5F5";

const ACTION_LABELS: Record<string, string> = {
  PRIVATE_REPLY: "Private reply",
  REQUIRE_FOLLOW: "Follow gate",
  PUBLIC_REPLY: "Public reply",
};

const truncate = (value: string, max: number) =>
  value.length > max ? `${value.slice(0, max)}…` : value;

const bodyOf = (message: DmMessageRow): { text: string; placeholder: boolean } => {
  if (message.text) return { text: message.text, placeholder: false };
  if (message.attachmentType) {
    return { text: `[${message.attachmentType}]`, placeholder: true };
  }
  if (message.postbackTitle) {
    return { text: `[Button] ${message.postbackTitle}`, placeholder: true };
  }
  return { text: "(no text)", placeholder: true };
};

const metaOf = (message: DmMessageRow): string[] => {
  const parts: string[] = [];
  if (message.source === "markit") {
    if (message.action) parts.push(ACTION_LABELS[message.action] ?? message.action);
    if (message.automationId) {
      parts.push(`Automation ${truncate(message.automationId, 6)}`);
    }
    if (message.commentText) {
      parts.push(`Comment: “${truncate(message.commentText, 60)}”`);
    }
  } else if (message.source === "creator") {
    parts.push("Creator message");
  } else {
    if (message.storyReplyId) parts.push("Story reply");
    else if (message.replyToMid) parts.push("Inline reply");
    if (message.postbackTitle && message.text) {
      parts.push(`Button: ${message.postbackTitle}`);
    }
  }
  return parts;
};

// "Failed (code 100 / 2534023)" — Meta's code, then its subcode when it sent one.
const failureLabel = (message: DmMessageRow): string => {
  if (message.errorCode === undefined) return "Failed";
  const subcode = message.errorSubcode !== undefined ? ` / ${message.errorSubcode}` : "";
  return `Failed (code ${message.errorCode}${subcode})`;
};

/**
 * One message in a thread. Fan on the left in white; Markit's own sends on the
 * right in a blue tint; the creator's manual messages on the right in grey. The
 * meta line carries what the send was (automation, triggering comment) and, for
 * a failure, Meta's error code — which is the part the page exists to surface.
 */
const DmBubble = ({ message }: { message: DmMessageRow }) => {
  const isFan = message.source === "fan";
  const isMarkit = message.source === "markit";
  const failed = message.status === "failed";
  const body = bodyOf(message);
  const meta = metaOf(message);

  const background = isFan ? colors.surface : isMarkit ? MARKIT_BG : colors.surfaceMuted;
  const border = failed
    ? colors.stateFailed
    : isFan
    ? colors.border
    : isMarkit
    ? MARKIT_BORDER
    : colors.borderStrong;

  return (
    <div
      style={{
        display: "flex",
        justifyContent: isFan ? "flex-start" : "flex-end",
      }}
    >
      <div
        style={{
          maxWidth: "78%",
          minWidth: 0,
          background,
          border: `1px solid ${border}`,
          borderRadius: 12,
          padding: "8px 12px",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <div
          style={{
            ...type.bodyRegular,
            color: body.placeholder ? colors.inkMuted : colors.ink,
            fontStyle: body.placeholder ? "italic" : "normal",
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
          }}
        >
          {body.text}
        </div>
        <div
          style={{
            ...type.bodySmallRegular,
            color: colors.inkFaint,
            display: "flex",
            flexWrap: "wrap",
            gap: "2px 8px",
          }}
        >
          <span>{formatDateTime(message.sentAt)}</span>
          {meta.map(part => (
            <span key={part} style={{ overflowWrap: "anywhere" }}>
              {part}
            </span>
          ))}
          {failed && (
            <span style={{ color: viz.critical, fontWeight: 600 }}>{failureLabel(message)}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default DmBubble;
